import { useRef } from "react";
import { createContext, useContext, useState, useEffect } from "react";
import { useUserStore } from "./useUserStore";
import { useSchoolsStore } from "./useSchoolsStore";
import SpecificAPI from "../services/SpecificApi";

const DepartmentsContext = createContext(null);

export const DepartmentsProvider = ({ children }) => {
  const hasLoadedRef = useRef(false);
  const { appUser, isAppUserLoading } = useUserStore();
  const { schools, isSchoolsLoading } = useSchoolsStore();

  const [departments, setDepartments] = useState([]);
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAppUserLoading || isSchoolsLoading) return;

    // 🔁 reset on logout
    if (!appUser || schools.length === 0) {
      hasLoadedRef.current = false;
      setDepartments([]);
      setIsDepartmentsLoading(false);
      return;
    }

    // 🛑 fetch once
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadDepartments = async () => {
      try {
        setIsDepartmentsLoading(true);

        console.log("Loading departments for schools: ", schools[0]?.id);
        const response = await SpecificAPI.getDepartments({}, schools[0]?.id);
        const departmentsData = response.data || [];

        const filtered = departmentsData.filter((dept) =>
          appUser.school_id.includes(dept.school_id),
        );

        setDepartments(filtered);
      } catch (err) {
        console.error("Failed to load departments:", err);
        setError(err);
      } finally {
        setIsDepartmentsLoading(false);
      }
    };

    loadDepartments();
  }, [appUser, isAppUserLoading, isSchoolsLoading, schools]);

  const loadDepartmentsBySchool = async (selectedSchool) => {
    try {
      setIsDepartmentsLoading(true);

      const response = await SpecificAPI.getDepartments({}, selectedSchool);
      const departmentsData = response.data || [];

      const filtered = departmentsData.filter((dept) =>
        appUser.school_id.includes(dept.school_id),
      );

      setDepartments(filtered);
    } catch (err) {
      console.error("Failed to load departments:", err);
      setError(err);
    } finally {
      setIsDepartmentsLoading(false);
    }
  };

  return (
    <DepartmentsContext.Provider
      value={{
        departments,
        isDepartmentsLoading,
        error,
        setDepartments,
        loadDepartmentsBySchool,
      }}
    >
      {children}
    </DepartmentsContext.Provider>
  );
};

export const useDepartmentsStore = () => useContext(DepartmentsContext);
